import React, { useState } from "react";
import { Badge, Box, CloseButton, Dialog, Flex, HStack, Icon, IconButton, Text } from "@chakra-ui/react";
import { SiTether } from "react-icons/si";
import { BsBank2 } from "react-icons/bs";
import { FiCheck, FiCopy } from "react-icons/fi";
import { formatDateTime } from "../../Utils/newHelper";

const STATUS_PALETTE = { accepted: "green", pending: "yellow", rejected: "red", processing: "blue" };

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      aria-label="Copy"
      size="2xs"
      variant="ghost"
      color={copied ? "green.500" : "gray.400"}
      _hover={{ color: copied ? "green.500" : "gray.700", bg: "gray.100" }}
      flexShrink={0}
      onClick={() => {
        navigator.clipboard?.writeText(String(value));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? <FiCheck /> : <FiCopy />}
    </IconButton>
  );
};

// One label/value row. Long values (wallet addresses, ids) wrap inside the
// card instead of overflowing, and can be copied.
const DetailRow = ({ label, value, mono, copyable }) => {
  const empty = value === null || value === undefined || value === "";
  return (
    <Flex
      gap={3}
      py={2.5}
      align="flex-start"
      borderBottomWidth="1px"
      borderColor="gray.100"
      _last={{ borderBottomWidth: 0 }}
    >
      <Text fontSize="xs" color="gray.500" width="38%" flexShrink={0} pt="1px">
        {label}
      </Text>
      <Flex flex="1" minW={0} align="flex-start" gap={1}>
        <Text
          flex="1"
          minW={0}
          fontSize="sm"
          fontWeight="medium"
          color={empty ? "gray.400" : "gray.800"}
          fontFamily={mono && !empty ? "mono" : undefined}
          wordBreak="break-all"
          lineHeight="1.5"
        >
          {empty ? "—" : value}
        </Text>
        {copyable && !empty && <CopyButton value={value} />}
      </Flex>
    </Flex>
  );
};

const Section = ({ title, children }) => (
  <Box borderWidth="1px" borderColor="gray.200" rounded="lg" px={4} py={1} bg="white">
    <Text fontSize="2xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="gray.500" pt={3} pb={1}>
      {title}
    </Text>
    {children}
  </Box>
);

export const ExternalWithdrawalInfoModal = ({ data, open, setOpen }) => {
  const isBank = data?.method === "bank";
  const accent = isBank ? "#2563EB" : "#26A17B";

  return (
    <Dialog.Root
      size="md"
      placement="center"
      motionPreset="scale"
      scrollBehavior="inside"
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content rounded="xl" overflow="hidden">
          <Dialog.Header px={6} pt={6} pb={4} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
            <HStack gap={3} align="center" width="100%" pr={8}>
              <Flex
                align="center"
                justify="center"
                boxSize="44px"
                rounded="lg"
                color="white"
                flexShrink={0}
                style={{ background: accent }}
              >
                <Icon fontSize="xl">{isBank ? <BsBank2 /> : <SiTether />}</Icon>
              </Flex>
              <Box minW={0} flex="1">
                <Dialog.Title fontSize="md" fontWeight="semibold">
                  {isBank ? "Bank Withdrawal" : "Crypto Withdrawal"}
                </Dialog.Title>
                <Text fontSize="xs" color="gray.500" fontFamily="mono" truncate>
                  {data?.reference}
                </Text>
              </Box>
              <Badge variant="surface" colorPalette={STATUS_PALETTE[data?.status] || "gray"} textTransform="capitalize">
                {data?.status}
              </Badge>
            </HStack>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" position="absolute" top={3} right={3} />
            </Dialog.CloseTrigger>
          </Dialog.Header>

          <Dialog.Body px={6} py={5} display="flex" flexDirection="column" gap={4}>
            <Flex
              justify="space-between"
              align="center"
              px={4}
              py={3}
              rounded="lg"
              borderWidth="1px"
              borderColor="gray.200"
              bg="gray.50"
            >
              <Box>
                <Text fontSize="xs" color="gray.500">Withdrawal amount</Text>
                <Text fontSize="2xl" fontWeight="bold" lineHeight="1.2">
                  {Number(data?.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                  <Text as="span" fontSize="sm" fontWeight="medium" color="gray.500">{data?.currency || "USD"}</Text>
                </Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" color="gray.500">Requested on</Text>
                <Text fontSize="xs" fontWeight="medium">{data?.createdAt ? formatDateTime(data.createdAt) : "—"}</Text>
              </Box>
            </Flex>

            <Section title={isBank ? "Bank details" : "Wallet details"}>
              {isBank ? (
                <>
                  <DetailRow label="Account holder" value={data?.accountHolderName} />
                  <DetailRow label="Account no." value={data?.accountNo} mono copyable />
                  <DetailRow label="Bank name" value={data?.branchName} />
                  <DetailRow label="IBAN" value={data?.iban} mono copyable />
                  <DetailRow label="Swift/BIC" value={data?.ifsc} mono copyable />
                </>
              ) : (
                <>
                  <DetailRow label="Wallet address" value={data?.walletAddress} mono copyable />
                  <DetailRow label="Network" value={data?.chain ? `USDT · ${data.chain}` : null} />
                </>
              )}
            </Section>

            <Section title="Request details">
              <DetailRow label="User ID" value={data?.externalUserId} mono copyable />
              <DetailRow label="Transaction ID" value={data?.transactionId} mono copyable />
              <DetailRow label="Email" value={data?.email} copyable />
              <DetailRow label="IP address" value={data?.ipaddress} mono />
              {data?.rejectReason && <DetailRow label="Reject reason" value={data.rejectReason} />}
              {data?.partnerError && <DetailRow label="Last provider error" value={data.partnerError} />}
            </Section>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
